<template>
    <div v-if="block" class="lib-row" :class="{ readonly }" :style="!!$listeners.click ? 'cursor: pointer' : null"
        @click="$emit('click')" @mousedown="$emit('mousedown', block, $event)">
        <div v-if="showArrows" class="lib-arrow" @click.stop="$emit('click:prev')">
            <v-icon size="20">mdi-chevron-left</v-icon>
        </div>
        <div v-if="block.img" class="lib-thumb" :style="{ backgroundImage: `url('${block.img}')` }" />
        <div v-else class="lib-icon">
            <v-icon size="20" color="accent">mdi-view-dashboard-outline</v-icon>
        </div>
        <div class="lib-info">
            <div class="lib-name text-truncate">{{ blockNameLabel(block.name) }}</div>
            <div class="lib-meta text-truncate">
                {{ block.block.exercises.length }}
                <span class="text-lowercase">{{ $tc(`common.exercises`, 2) }}</span>
                <template v-if="!noDurationText"> &middot; {{ blockDuration }}</template>
                <v-btn v-if="showHover" icon x-small class="ml-1" @click.stop="showPreview = !showPreview"
                    :disabled="!block" color="secondary">
                    <v-icon size="15">mdi-eye</v-icon>
                </v-btn>
            </div>
            <v-dialog v-if="showHover" v-model="showPreview" @click:outside="showPreview = false" max-width="300">
                <v-card flat class="block-exercises-container pa-3">
                    <exercise-event v-for="(item, i) in blockExercises" :key="i" :event="item" is-hover-view />
                </v-card>
            </v-dialog>
        </div>
        <div v-if="badge" class="lib-badge">{{ badge }}</div>
        <v-menu offset-y v-if="withMenu">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="lib-more" v-bind="attrs" v-on="on" @click.stop @mousedown.stop>
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                </button>
            </template>
            <v-list dense>
                <v-list-item link @click="$emit('click:edit')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                    <v-list-item-title>{{ $t('common.edit_block') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="$emit('click:copy')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                    <v-list-item-title>{{ $t('common.copy_block') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="$emit('click:share')" v-if="canShare">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-share-variant</v-icon>
                    <v-list-item-title>{{ $t('common.share_block') }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item link @click="$emit('click:delete')">
                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                    <v-list-item-title class="error--text">{{ $t('common.delete_block') }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
        <div v-if="showArrows" class="lib-arrow" @click.stop="$emit('click:next')">
            <v-icon size="20">mdi-chevron-right</v-icon>
        </div>
    </div>
</template>

<script>
import { clearBlock } from '../../../utils/Exercises';
import ExerciseEvent from '../../calendar/ExerciseEvent.vue'

export default {
    components: { ExerciseEvent },
    props: {
        block: {
            type: Object,
            default: null,
        },
        withMenu: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        showArrows: {
            type: Boolean,
            default: false
        },
        showHover: {
            type: Boolean,
            default: false
        },
        noDurationText: {
            type: Boolean,
            default: false
        },
        canShare: {
            type: Boolean,
            default: false
        },
        badge: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            blockColor: '#66CCCC',
            showPreview: false,
        }
    },
    computed: {
        minutes(vm = this) {
            let minutes = 0
            vm.block?.block?.exercises?.forEach(exercise => {
                minutes += exercise?.config?.sets?.length || vm.block.sets || 0
            })
            return minutes
        },
        blockImg(vm = this) {
            return vm.block.img || vm.imgPlaceholder
        },
        blockDuration(vm = this) {
            if (!vm.minutes) return vm.$t('common.not_available_short')
            let minutesString = vm.$tc(`common.minutes`, Number(vm.minutes) === 1 ? 1 : 2).toLowerCase()
            let minutesAdded = Number(vm.minutes * 1.3).toFixed()
            if (Number(vm.minutes) === Number(minutesAdded)) return `${vm.minutes} ${minutesString}`
            return `${vm.minutes} - ${minutesAdded} ${minutesString}`
        },
        isLgScreen(vm = this) {
            return vm.$vuetify.breakpoint.lgAndUp
        },
        contentCols(vm = this) {
            if (vm.showArrows) return vm.isLgScreen ? 8 : 7
            return 9
        },
        blockExercises(vm = this) {
            let block = clearBlock(vm.block?.block || null)
            let exercises = []
            block?.exercises?.forEach(exercise => {
                if (exercise.type === 'exercise') exercises.push({
                    event: {
                        ...exercise,
                        color: vm.blockColor,
                        isCircuit: block?.isCircuit || false,
                        sets: block?.sets || null,
                    }
                })
            })
            return exercises
        },
    },
}
</script>

<style scoped>
.lib-row {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all .15s;
}

.lib-row:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.lib-row.readonly {
    pointer-events: none;
}

.lib-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lib-thumb {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    background-color: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.lib-info {
    flex: 1;
    min-width: 0;
}

.lib-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.lib-meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
}

.lib-more {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    transition: all .15s;
}

.lib-more:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.lib-more .v-icon {
    color: inherit !important;
}

.lib-badge {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    background: var(--v-primary-base);
    color: #0C0E12;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lib-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.lib-arrow:hover {
    color: var(--v-txtPrimary-base);
}

.block-exercises-container {
    background: var(--v-surface-base) !important;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
}
</style>